import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Type } from '../types/Type';
import { Hersteller } from '../types/Hersteller';
import { OnboardingArticle } from '../types/Article';
import { Font } from '../types/Font';
import { fetchTypes } from '../services/TypeService';
import { fetchHerstellers } from '../services/HerstellerService';
import { showToast } from '../services/ToastService';

export type Logo = {
    id: string;
    file: File | null;
    preview: string;
    label: string;
    fileExtension?: string;
};

type OnboardingFormData = {
    selectedTypes: Type[];
    selectedHerstellers: Hersteller[];
    selectedArticles: OnboardingArticle[];
    primaryColor: string;
    secondaryColor: string;
    primaryColorName: string;
    secondaryColorName: string;
    font: string;
    style: string;
    fontPreviewText: string;
    selectedFont: Font | null;
    logos: Logo[];
    userData: {
        company: string;
        firstName: string;
        lastName: string;
        customTypeName: string;
        street: string;
        houseNumber: string;
        postalCode: string;
        city: string;
        email: string;
        phoneNumber: string;
        memberCount: string;
        additionalInformation: string;
    };
};

type OnboardingContextType = {
    formData: OnboardingFormData;
    types: Type[];
    herstellers: Hersteller[];
    typesLoading: boolean;
    herstellersLoading: boolean;
    loadTypes: () => Promise<void>;
    loadHerstellers: (branchCategoryIds: number[], categoryId?: number | null) => Promise<void>;
    toggleSelectedHersteller: (h: Hersteller) => void;
    toggleType: (type: Type) => void;
    setSelectedTypes: (types: Type[]) => void;
    setSelectedArticles: (articles: OnboardingArticle[]) => void;
    setPrimaryColor: (color: string) => void;
    setSecondaryColor: (color: string) => void;
    setPrimaryColorName: (name: string) => void;
    setSecondaryColorName: (name: string) => void;
    setFont: (font: string) => void;
    setStyle: (style: string) => void;
    setFontPreviewText: (text: string) => void;
    setSelectedFont: (font: Font | null) => void;
    addLogo: () => void;
    removeLogo: (id: string) => void;
    updateLogo: (id: string, updates: Partial<Logo>) => void;
    setUserDataField: (field: string, value: string | boolean) => void;
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

type OnboardingContextProps = {
    children: React.ReactNode | React.ReactNode[];
};

export const OnboardingContextProvider = ({
    children,
}: OnboardingContextProps): JSX.Element => {
    const [formData, setFormData] = useState<OnboardingFormData>({
        selectedTypes: [],
        selectedHerstellers: [],
        selectedArticles: [],
        primaryColor: '',
        secondaryColor: '',
        primaryColorName: '',
        secondaryColorName: '',
        font: '',
        style: '',
        fontPreviewText: 'workwear',
        selectedFont: null,
        logos: [],
        userData: {
            company: '',
            firstName: '',
            lastName: '',
            customTypeName: '',
            street: '',
            houseNumber: '',
            postalCode: '',
            city: '',
            email: '',
            phoneNumber: '',
            memberCount: '',
            additionalInformation: '',
        },
    });

    const [types, setTypes] = useState<Type[]>([]);
    const [herstellers, setHerstellers] = useState<Hersteller[]>([]);
    const [typesLoading, setTypesLoading] = useState(false);
    const [herstellersLoading, setHerstellersLoading] = useState(false);

    const loadTypes = useCallback(async () => {
        if (types.length > 0) return;
        setTypesLoading(true);
        try {
            const fetchedTypes = await fetchTypes();
            setTypes(fetchedTypes);
        } catch (error) {
            console.error('Failed to load types:', error);
            showToast('Fehler beim Laden der Typen', 'error');
        } finally {
            setTypesLoading(false);
        }
    }, [types.length]);

    const herstellerScopeKeyRef = useRef<string | null>(null);
    const herstellerRequestIdRef = useRef(0);
    const loadHerstellers = useCallback(async (
        branchCategoryIds: number[],
        categoryId: number | null = null
    ) => {
        const scopeKey = `${[...branchCategoryIds].sort((a, b) => a - b).join(',')}|${categoryId ?? ''}`;
        if (herstellerScopeKeyRef.current === scopeKey) {
            return;
        }

        const requestId = ++herstellerRequestIdRef.current;
        herstellerScopeKeyRef.current = scopeKey;
        setHerstellersLoading(true);

        try {
            const fetched = await fetchHerstellers({ branchCategoryIds, categoryId });
            if (requestId !== herstellerRequestIdRef.current) {
                return;
            }

            setHerstellers(fetched);

            const allowedIds = new Set(fetched.map((h) => h.id));
            setFormData((prev) => {
                const nextHerstellers = prev.selectedHerstellers.filter((h) => allowedIds.has(h.id));
                if (nextHerstellers.length === prev.selectedHerstellers.length) {
                    return prev;
                }
                return {
                    ...prev,
                    selectedHerstellers: nextHerstellers,
                };
            });
        } catch (error) {
            if (requestId !== herstellerRequestIdRef.current) {
                return;
            }
            console.error('Failed to load herstellers:', error);
            showToast('Fehler beim Laden der Hersteller', 'error');
            setHerstellers([]);
            herstellerScopeKeyRef.current = null;
        } finally {
            if (requestId === herstellerRequestIdRef.current) {
                setHerstellersLoading(false);
            }
        }
    }, []);

    const toggleSelectedHersteller = useCallback((h: Hersteller) => {
        setFormData((prev) => {
            const isSelected = prev.selectedHerstellers.some((x) => x.id === h.id);
            return {
                ...prev,
                selectedHerstellers: isSelected
                    ? prev.selectedHerstellers.filter((x) => x.id !== h.id)
                    : [...prev.selectedHerstellers, h],
            };
        });
    }, []);

    const CUSTOM_TYPE_NAME = 'Andere branche';

    const setSelectedTypes = useCallback((types: Type[]) => {
        setFormData((prev) => ({
            ...prev,
            selectedTypes: types,
            selectedArticles: [],
        }));
    }, []);

    const toggleType = (type: Type) => {
        setFormData((prev) => {
            const isSelected = prev.selectedTypes.some(t => t.id === type.id);
            const isRemovingCustomType = isSelected && type.name === CUSTOM_TYPE_NAME;

            if (isSelected) {
                return {
                    ...prev,
                    selectedTypes: prev.selectedTypes.filter(t => t.id !== type.id),
                    selectedArticles: [],
                    userData: isRemovingCustomType
                        ? { ...prev.userData, customTypeName: '' }
                        : prev.userData,
                };
            }
            return {
                ...prev,
                selectedTypes: [...prev.selectedTypes, type],
                selectedArticles: [],
            };
        });
    };

    const setPrimaryColor = (color: string) => {
        setFormData((prev) => ({
            ...prev,
            primaryColor: color,
        }));
    };

    const setSecondaryColor = (color: string) => {
        setFormData((prev) => ({
            ...prev,
            secondaryColor: color,
        }));
    };

    const setPrimaryColorName = (name: string) => {
        setFormData((prev) => ({
            ...prev,
            primaryColorName: name,
        }));
    };

    const setSecondaryColorName = (name: string) => {
        setFormData((prev) => ({
            ...prev,
            secondaryColorName: name,
        }));
    };

    const setFont = (font: string) => {
        setFormData((prev) => ({
            ...prev,
            font: font,
        }));
    };

    const setStyle = (style: string) => {
        setFormData((prev) => ({
            ...prev,
            style: style,
        }));
    };

    const setFontPreviewText = (text: string) => {
        setFormData((prev) => ({
            ...prev,
            fontPreviewText: text,
        }));
    };

    const setSelectedFont = (font: Font | null) => {
        setFormData((prev) => ({
            ...prev,
            selectedFont: font,
        }));
    };

    const addLogo = () => {
        const newLogo: Logo = {
            id: Date.now().toString(),
            file: null,
            preview: '',
            label: '',
            fileExtension: '',
        };
        setFormData((prev) => ({
            ...prev,
            logos: [...prev.logos, newLogo],
        }));
    };

    const removeLogo = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            logos: prev.logos.filter((logo) => logo.id !== id),
        }));
    };

    const updateLogo = (id: string, updates: Partial<Logo>) => {
        setFormData((prev) => ({
            ...prev,
            logos: prev.logos.map((logo) =>
                logo.id === id ? { ...logo, ...updates } : logo
            ),
        }));
    };

    const setUserDataField = (field: string, value: string | boolean) => {
        setFormData((prev) => ({
            ...prev,
            userData: {
                ...prev.userData,
                [field]: value,
            },
        }));
    };

    const setSelectedArticles = (articles: OnboardingArticle[]) => {
        setFormData((prev) => ({
            ...prev,
            selectedArticles: articles,
        }));
    };

    return (
        <OnboardingContext.Provider
            value={{
                formData,
                types,
                herstellers,
                typesLoading,
                herstellersLoading,
                loadTypes,
                loadHerstellers,
                toggleSelectedHersteller,
                toggleType,
                setSelectedTypes,
                setSelectedArticles,
                setPrimaryColor,
                setSecondaryColor,
                setPrimaryColorName,
                setSecondaryColorName,
                setFont,
                setStyle,
                setFontPreviewText,
                setSelectedFont,
                addLogo,
                removeLogo,
                updateLogo,
                setUserDataField,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboardingContext = () => {
    const data = useContext(OnboardingContext);

    if (data === undefined) {
        throw new Error(
            'useOnboardingContext must be used within OnboardingContextProvider'
        );
    }

    return data;
};
